// The show schedule: the hours the hub plays the street's songs by itself,
// in order (repeating) or shuffled. Pure functions, shared by the hub (which
// runs it), the admin page (which edits it) and the public page (which shows
// the hours).

export type ScheduleOrder = "loop" | "shuffle";

export interface ShowSchedule {
  enabled: boolean;
  // Local times, "HH:MM" (24-hour). An end before the start runs past midnight.
  start: string;
  end: string;
  // Days the show starts on: 0 = Sunday ... 6 = Saturday.
  days: number[];
  order: ScheduleOrder;
  // Songs to play, in order; empty = every analyzed song in the library.
  songIds: string[];
  // IANA time zone the times are in, e.g. "America/Los_Angeles".
  timeZone: string;
  // "Stop for tonight": no new songs until this time (epoch ms).
  pausedUntil?: number;
}

export const DEFAULT_SCHEDULE: ShowSchedule = {
  enabled: false,
  start: "17:00",
  end: "22:00",
  days: [0, 1, 2, 3, 4, 5, 6],
  order: "loop",
  songIds: [],
  timeZone: "America/Los_Angeles",
};

export class ScheduleError extends Error {}

const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;
const SONG_ID = /^[0-9a-f]{16}$/;

export function minutesOf(time: string): number {
  const m = TIME.exec(time);
  if (!m) throw new ScheduleError(`"${time}" isn't a time like 17:00`);
  return Number(m[1]) * 60 + Number(m[2]);
}

export function validTimeZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

// Checks a schedule from the admin page; throws ScheduleError with a message
// fit to show.
export function parseSchedule(value: unknown): ShowSchedule {
  if (!value || typeof value !== "object") throw new ScheduleError("schedule missing");
  const v = value as Record<string, unknown>;
  if (typeof v.enabled !== "boolean") throw new ScheduleError("enabled must be true or false");
  if (typeof v.start !== "string" || typeof v.end !== "string") throw new ScheduleError("start and end times are needed");
  if (minutesOf(v.start) === minutesOf(v.end)) throw new ScheduleError("The show needs to end at a different time than it starts.");
  if (!Array.isArray(v.days) || v.days.some((d) => !Number.isInteger(d) || d < 0 || d > 6)) {
    throw new ScheduleError("days must be numbers 0 (Sunday) to 6 (Saturday)");
  }
  const days = [...new Set(v.days as number[])].sort();
  if (v.enabled && days.length === 0) throw new ScheduleError("Pick at least one day for the show.");
  if (v.order !== "loop" && v.order !== "shuffle") throw new ScheduleError("order must be loop or shuffle");
  if (!Array.isArray(v.songIds) || v.songIds.length > 500 || v.songIds.some((id) => typeof id !== "string" || !SONG_ID.test(id))) {
    throw new ScheduleError("songIds must be a list of song ids");
  }
  if (typeof v.timeZone !== "string" || v.timeZone.length > 64 || !validTimeZone(v.timeZone)) {
    throw new ScheduleError("unknown time zone");
  }
  return {
    enabled: v.enabled,
    start: v.start,
    end: v.end,
    days,
    order: v.order,
    songIds: [...new Set(v.songIds as string[])],
    timeZone: v.timeZone,
  };
}

// Day of the week (0 = Sunday) and minutes after midnight at `at`, in `zone`.
export function localTime(at: number, zone: string): { day: number; minutes: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(at));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const day = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(get("weekday"));
  return { day, minutes: Number(get("hour")) * 60 + Number(get("minute")) };
}

// Whether show hours are on at `at` (ignoring enabled and pausedUntil).
export function inShowHours(schedule: ShowSchedule, at: number): boolean {
  const { day, minutes } = localTime(at, schedule.timeZone);
  const start = minutesOf(schedule.start);
  const end = minutesOf(schedule.end);
  if (start < end) return schedule.days.includes(day) && minutes >= start && minutes < end;
  // Past midnight: the evening part on a show day, or the early morning
  // after one.
  return (schedule.days.includes(day) && minutes >= start) || (schedule.days.includes((day + 6) % 7) && minutes < end);
}

// Whether the hub should be starting songs at `at`.
export function showRunning(schedule: ShowSchedule | undefined, at: number): boolean {
  if (!schedule?.enabled) return false;
  if (schedule.pausedUntil && at < schedule.pausedUntil) return false;
  return inShowHours(schedule, at);
}

// The next minute at or after `from` where the show hours change from
// `running` to not (or back), searching up to 8 days. For "stop for tonight"
// and "starts at ..." on the admin page.
export function nextChange(schedule: ShowSchedule, from: number, running: boolean): number | null {
  const minute = 60_000;
  let t = Math.ceil(from / minute) * minute;
  for (let i = 0; i < 8 * 24 * 60; i++, t += minute) {
    if (inShowHours(schedule, t) !== running) return t;
  }
  return null;
}

// "5:00pm"
export function formatTime(time: string): string {
  const minutes = minutesOf(time);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, "0")}${h < 12 ? "am" : "pm"}`;
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// "The show plays between 5:00pm and 10:00pm" (+ " (Fri, Sat, Sun)" when it
// isn't every day). Days are listed Monday first.
export function describeHours(schedule: Pick<ShowSchedule, "start" | "end" | "days">): string {
  const base = `The show plays between ${formatTime(schedule.start)} and ${formatTime(schedule.end)}`;
  if (schedule.days.length === 7) return base;
  const days = [1, 2, 3, 4, 5, 6, 0].filter((d) => schedule.days.includes(d)).map((d) => DAY_NAMES[d]);
  return `${base} (${days.join(", ")})`;
}

// Picks the songs to play, in order. `queue` is the shuffle's remaining songs
// (the hub keeps it between calls); returns the next song id and the new queue.
export function nextSong(
  order: ScheduleOrder,
  songs: string[],
  last: string | null,
  queue: string[],
  random: () => number = Math.random,
): { songId: string | null; queue: string[] } {
  if (songs.length === 0) return { songId: null, queue: [] };
  if (order === "loop") {
    const i = last === null ? -1 : songs.indexOf(last);
    return { songId: songs[(i + 1) % songs.length]!, queue: [] };
  }
  // Shuffle: every song once before any repeats, never the same twice in a row.
  let remaining = queue.filter((id) => songs.includes(id));
  if (remaining.length === 0) {
    remaining = [...songs];
    for (let i = remaining.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [remaining[i], remaining[j]] = [remaining[j]!, remaining[i]!];
    }
    if (remaining.length > 1 && remaining[0] === last) remaining.push(remaining.shift()!);
  }
  const [songId, ...rest] = remaining;
  return { songId: songId!, queue: rest };
}
